/**
 * Core Carrom Game Types
 *
 * All TypeScript types and interfaces for the Carrom game.
 * Board: 600×600 square with 4 corner pockets.
 * Physics-based gameplay with striker flicking mechanic.
 */

// ============================================================================
// Player & Game Configuration
// ============================================================================

export type CarromPlayer = 'player1' | 'player2';
export type CarromDifficulty = 'easy' | 'medium' | 'hard';
export type PlayerType = 'human' | 'ai';
export type CoinType = 'white' | 'black' | 'queen' | 'striker';

/**
 * Powder type affects board friction.
 * - none: bare board, high friction (coins stop quickly)
 * - light: disco/boric powder, moderate friction (standard play)
 * - medium: generous powder, lower friction (smooth glide)
 * - heavy: tournament-grade, very low friction (fast, slippery)
 */
export type PowderType = 'none' | 'light' | 'medium' | 'heavy';

/** Speed preset multiplier for max shot velocity */
export type SpeedPreset = 'slow' | 'normal' | 'fast' | 'turbo';

/**
 * Coin layout pattern for initial placement.
 * - standard: hexagonal rings (inner 6 + outer 12) — tournament style
 * - horizontal: coins in a horizontal line through center
 * - vertical: coins in a vertical line through center
 */
export type CoinLayout = 'standard' | 'horizontal' | 'vertical';

export interface BoardSettings {
  powder: PowderType;
  speed: SpeedPreset;
  coinLayout: CoinLayout;
}

export const COIN_LAYOUT_CONFIG: Record<CoinLayout, { label: string; emoji: string; description: string }> = {
  standard:   { label: 'Standard',   emoji: '⬡', description: 'Hexagonal rings — tournament style' },
  horizontal: { label: 'Horizontal', emoji: '↔️', description: 'Coins in a horizontal line' },
  vertical:   { label: 'Vertical',   emoji: '↕️', description: 'Coins in a vertical line' },
};

export const POWDER_CONFIG: Record<PowderType, { friction: number; label: string; emoji: string; description: string }> = {
  none:   { friction: 0.94,  label: 'No Powder',     emoji: '🪵', description: 'Bare board — high friction, coins stop quickly' },
  light:  { friction: 0.97,  label: 'Light Powder',  emoji: '✨', description: 'Boric powder — standard play' },
  medium: { friction: 0.985, label: 'Medium Powder', emoji: '💫', description: 'Generous powder — smooth glide' },
  heavy:  { friction: 0.993, label: 'Heavy Powder',  emoji: '⚡', description: 'Tournament-grade — fast and slippery' },
};

export const SPEED_CONFIG: Record<SpeedPreset, { maxSpeed: number; label: string }> = {
  slow:   { maxSpeed: 1000, label: 'Slow' },
  normal: { maxSpeed: 1800, label: 'Normal' },
  fast:   { maxSpeed: 2600, label: 'Fast' },
  turbo:  { maxSpeed: 3500, label: 'Turbo' },
};

// ============================================================================
// Physics Types
// ============================================================================

export interface PhysicsBody {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  mass: number;
  isStatic: boolean;
  type: CoinType;
  pocketed: boolean;
}

export interface PhysicsConfig {
  boardSize: number;
  borderWidth: number;
  pocketRadius: number;
  friction: number;
  restitution: number;
  stopThreshold: number;
  pocketPositions: { x: number; y: number }[];
  maxSpeed: number;
}

export interface Vector2D {
  x: number;
  y: number;
}

// ============================================================================
// Coin & Striker State
// ============================================================================

export interface CoinState {
  id: string;
  type: CoinType;
  x: number;
  y: number;
  pocketed: boolean;
  pocketedBy?: CarromPlayer;
}

export interface StrikerState {
  x: number;
  y: number;
  baselineY: number;
}

// ============================================================================
// Shot Types
// ============================================================================

export interface ShotInput {
  strikerX: number;
  angle: number;
  power: number;
}

export interface ShotResult {
  pocketedCoins: CoinState[];
  strikerPocketed: boolean;
  foul: boolean;
  foulReason?: string;
  continueTurn: boolean;
  queenCovered: boolean;
}

// ============================================================================
// Game State
// ============================================================================

export type CarromGameStatus =
  | { type: 'active' }
  | { type: 'win'; winner: CarromPlayer }
  | { type: 'draw' };

export interface CarromGameState {
  gameId: string;
  coins: CoinState[];
  currentTurn: CarromPlayer;
  player1Score: number;
  player2Score: number;
  player1PocketedCoins: CoinState[];
  player2PocketedCoins: CoinState[];
  player1Assignment: 'white' | 'black';
  player2Assignment: 'white' | 'black';
  queenPocketed: boolean;
  queenCovered: boolean;
  queenPocketedBy?: CarromPlayer;
  pendingQueenCover: boolean;
  pendingQueenCoverPlayer?: CarromPlayer;
  gameStatus: CarromGameStatus;
  playerTypes: Record<CarromPlayer, PlayerType>;
  moveHistory: MoveRecord[];
  startedAt: number;
  lastMoveAt: number;
  consecutiveFouls: Record<CarromPlayer, number>;
}

export interface MoveRecord {
  moveNumber: number;
  player: CarromPlayer;
  shot: ShotInput;
  result: ShotResult;
  timestamp: number;
}

// ============================================================================
// Game Result & Statistics
// ============================================================================

export interface CarromGameResult {
  gameId: string;
  mode: CarromGameSettings['mode'];
  difficulty?: CarromDifficulty;
  winner?: CarromPlayer;
  reason: 'all_coins_pocketed' | 'resignation' | 'foul_limit';
  player1Score: number;
  player2Score: number;
  totalShots: number;
  duration: number;
  completedAt: number;
}

export interface CarromGameStats {
  wins: number;
  losses: number;
  draws: number;
  winPercentage: number;
  averageScore: number;
  totalGames: number;
}

export interface CarromStatistics {
  userId?: string;
  singlePlayer: {
    easy: CarromGameStats;
    medium: CarromGameStats;
    hard: CarromGameStats;
  };
  localMultiplayer: CarromGameStats;
  onlineMultiplayer: CarromGameStats;
  totalGames: number;
  totalPlayTime: number;
}

// ============================================================================
// Game Settings
// ============================================================================

export interface CarromGameSettings {
  mode: 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';
  aiDifficulty?: CarromDifficulty;
  playerCount?: 2 | 3 | 4;
}

// ============================================================================
// Component Props
// ============================================================================

export interface CarromGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

export interface CarromGameModeSelectorProps {
  onStartGame: (settings: CarromGameSettings) => void;
  onJoinPrivateGame?: (roomCode: string) => Promise<void>;
  guestGamesRemaining?: number;
  isAuthenticated?: boolean;
}

// ============================================================================
// Aiming State
// ============================================================================

export interface AimState {
  isAiming: boolean;
  isDraggingStriker: boolean;
  strikerX: number;
  angle: number;
  power: number;
  dragStartX: number;
  dragStartY: number;
}

// ============================================================================
// Board Constants
// ============================================================================

export const BOARD_SIZE = 600;
export const BORDER_WIDTH = 15;
export const PLAYABLE_SIZE = BOARD_SIZE - BORDER_WIDTH * 2;
export const POCKET_RADIUS = 22;
export const COIN_RADIUS = 12;
export const STRIKER_RADIUS = 16;
export const QUEEN_RADIUS = 12;
export const BASELINE_OFFSET = 100;
export const MAX_POWER = 1;
export const MAX_SPEED = 1800;
export const CENTER = BOARD_SIZE / 2;

export const POCKET_POSITIONS: Vector2D[] = [
  { x: BORDER_WIDTH, y: BORDER_WIDTH },
  { x: BOARD_SIZE - BORDER_WIDTH, y: BORDER_WIDTH },
  { x: BORDER_WIDTH, y: BOARD_SIZE - BORDER_WIDTH },
  { x: BOARD_SIZE - BORDER_WIDTH, y: BOARD_SIZE - BORDER_WIDTH },
];

export const DEFAULT_PHYSICS_CONFIG: PhysicsConfig = {
  boardSize: BOARD_SIZE,
  borderWidth: BORDER_WIDTH,
  pocketRadius: POCKET_RADIUS,
  friction: 0.97,
  restitution: 0.85,
  stopThreshold: 0.5,
  pocketPositions: POCKET_POSITIONS,
  maxSpeed: MAX_SPEED,
};
